/**
 * Value shapes for the six frozen field kinds, and the rules for turning a form
 * state into a payload the API will accept.
 *
 * The one thing to protect here: an empty field must reach the API as an
 * *empty* value, not be dropped. The API deletes cleared fields so the page
 * falls back to the committed content in hpn-landing — that fallback is the
 * guarantee the whole design rests on, and it is only reachable by sending the
 * cleared value.
 */

/**
 * The languages a manifest fallback can carry, in resolution order.
 *
 * Deliberately not imported from `languages.js`: that list is what the editor
 * offers, this one is the shape hpn-landing's generator emits. They happen to
 * match, and if a third language is ever added they must be changed together —
 * but a manifest generated before that change must still be readable.
 */
import {
  isListValue,
  listEqualsFallback,
  listPayload,
  listSearchText,
  sameListValue,
} from "./listValue";

const FALLBACK_LANGS = ["bg", "en"];

/**
 * Is this fallback the per-language `{ bg, en }` wrapper rather than a value?
 *
 * Safe to decide by keys because the composite kinds' own shapes share none of
 * them: `link` is `{label, href}`, `image` `{url, alt}`, `seo` `{title,
 * description}`. A wrapper needs at least one language key and nothing else, so
 * an empty object stays a value and cannot resolve to `undefined`.
 */
const isLocalisedFallback = (fallback) =>
  !!fallback &&
  typeof fallback === "object" &&
  !Array.isArray(fallback) &&
  Object.keys(fallback).length > 0 &&
  Object.keys(fallback).every((key) => FALLBACK_LANGS.includes(key));

/**
 * The committed value for the language being edited.
 *
 * Until DAB-836 the generator expanded the rules against the Bulgarian module
 * only and emitted one value per field, so the English tab advertised the
 * Bulgarian copy as "От сайта" — and DAB-829's equals-fallback normalisation
 * compared English edits against a Bulgarian string, so nothing was ever
 * recognised as untouched. It now emits `{ bg, en }`.
 *
 * The scalar branch is not a transition crutch: a manifest is only rewritten
 * when somebody runs `sync-manifests.mjs`, so an un-synced page must keep
 * behaving exactly as it did. Falling back to `bg` covers a field only the
 * Bulgarian module carries — the English site renders the Bulgarian string
 * there, so that is genuinely what "от сайта" means.
 */
export const resolveFallback = (fallback, lang) => {
  if (!isLocalisedFallback(fallback)) return fallback;
  return fallback[lang] !== undefined ? fallback[lang] : fallback.bg;
};

/**
 * A manifest whose every fallback has been resolved for one language.
 *
 * Resolving once, at load, is why nothing downstream — the placeholder, the
 * seed, "Върни текста от сайта", the equals-fallback normalisation, the search
 * index — has to know a fallback was ever per-language.
 */
export const localiseManifest = (manifest, lang) => ({
  ...manifest,
  // A `list` is edited differently per language — Bulgarian owns its structure
  // (DAB-852) — so the language travels with the manifest and its list fields.
  lang,
  fields: manifest.fields.map((field) => ({
    ...field,
    fallback: resolveFallback(field.fallback, lang),
    ...(field.kind === "list" ? { lang } : {}),
  })),
});

/** A composite is cleared by emptying its inputs, so every kind has an empty form. */
export const emptyValueForKind = (kind) => {
  switch (kind) {
    case "list":
      // Untouched: the site's own list. See listValue.js.
      return null;
    case "link":
      return { label: "", href: "" };
    case "image":
      return { url: "", alt: "" };
    case "seo":
      return { title: "", description: "" };
    default:
      return "";
  }
};

/**
 * Coerce a stored value into the shape the inputs expect. Stored drafts predate
 * nothing yet, but a manifest kind can change under an existing value (a `text`
 * promoted to `paragraph`, say), and a mismatched shape would otherwise crash
 * the input rather than degrade to empty.
 */
export const normaliseValue = (kind, value) => {
  if (kind === "list") {
    // TODO(DAB-852): delete after migratePageLists has run on staging and prod
    // `professionals.featuredIds` was a comma-separated text field before
    // DAB-852. Read that shape as items until `migratePageLists.ts` has run,
    // so saving the page cannot silently clear the row.
    if (typeof value === "string") {
      const ids = [...new Set(value.split(",").map((id) => id.trim()).filter(Boolean))];
      return ids.length ? { items: ids.map((id) => ({ id })) } : null;
    }
    return isListValue(value) ? value : null;
  }
  const empty = emptyValueForKind(kind);
  if (value === null || value === undefined) return empty;

  if (typeof empty === "string") {
    return typeof value === "string" ? value : empty;
  }
  if (typeof value !== "object" || Array.isArray(value)) return empty;

  return Object.keys(empty).reduce((acc, part) => {
    acc[part] = typeof value[part] === "string" ? value[part] : "";
    return acc;
  }, {});
};

/**
 * Mirrors `isClearedForKind` in the API controller. Must be kind-aware: a
 * cleared `link` is `{label: '', href: ''}` — a truthy object — and a generic
 * falsy check would report it as set.
 */
export const isCleared = (kind, value) => {
  const normalised = normaliseValue(kind, value);
  if (kind === "list") return !normalised || normalised.items.length === 0;
  if (typeof normalised === "string") return normalised === "";
  return Object.values(normalised).every((part) => part === "");
};

/** The stored draft, or the empty form, for every editable field in the manifest. */
export const initialValuesFromManifest = (manifest, page) => {
  const draft = page?.draftFields || {};
  return manifest.fields.reduce((acc, field) => {
    acc[field.path] = normaliseValue(field.kind, draft[field.path]);
    return acc;
  }, {});
};

/**
 * Is this value, part for part, the committed content it would fall back to?
 *
 * Kind-aware for the same reason `isCleared` is: composites have to be compared
 * per part, and both sides go through `normaliseValue` first so a missing part
 * and an empty one compare equal. For `richtext` this is an exact match on the
 * raw HTML — good enough, because the only way a field arrives here holding the
 * fallback verbatim is that the editor was seeded with it and never typed.
 */
export const valueEqualsFallback = (kind, value, fallback) => {
  if (kind === "list") return sameListValue(value, fallback);
  const current = normaliseValue(kind, value);
  const committed = normaliseValue(kind, fallback);
  if (typeof current === "string") return current === committed;
  return Object.keys(current).every(
    (part) => current[part] === committed[part],
  );
};

/**
 * The save payload.
 *
 * Locked fields are excluded rather than sent unchanged: they are structural
 * values shown for context (app deep-link hrefs, `specialistType` keys) and the
 * API rejects the whole request if one appears. Unknown paths are impossible
 * here because the payload is built from the manifest itself.
 *
 * Anything that merely *equals* its fallback is sent cleared. The editor seeds
 * an input with the committed text when you press Edit, so without this pass an
 * opened-then-abandoned field would store a copy of that text and silently take
 * ownership of it away from hpn-landing. Clearing keeps the API contract exactly
 * as it was — the API deletes cleared fields and the page falls back — while the
 * form is free to show real text instead of a blank box.
 */
export const payloadFromValues = (manifest, values, structures = {}) =>
  manifest.fields.reduce((acc, field) => {
    if (field.locked) return acc;
    if (field.kind === "list") {
      // `structures`: the Bulgarian draft per list path, for a non-Bulgarian
      // tab — English is sent only for the ids that tab displays.
      acc[field.path] = listPayload(
        field,
        values[field.path],
        manifest.lang || field.lang || "bg",
        structures[field.path],
      );
      return acc;
    }
    const value = normaliseValue(field.kind, values[field.path]);
    acc[field.path] = valueEqualsFallback(field.kind, value, field.fallback)
      ? emptyValueForKind(field.kind)
      : value;
    return acc;
  }, {});

/**
 * What to put in the input when a field moves from its default state into
 * editing: the stored draft when there is one, otherwise the committed text, so
 * the editor amends real copy rather than retyping it. `payloadFromValues`
 * un-does the seeding for anything left untouched.
 */
export const seedValueForEditing = (kind, value, fallback) =>
  isCleared(kind, value)
    ? normaliseValue(kind, fallback)
    : normaliseValue(kind, value);

/** A field is overridden when it holds text of its own — not cleared, not the fallback. */
export const isFieldOverridden = (field, value) => {
  if (field.locked) return false;
  if (field.kind === "list") {
    return !listEqualsFallback(field, value, field.lang || "bg");
  }
  return (
    !isCleared(field.kind, value) &&
    !valueEqualsFallback(field.kind, value, field.fallback)
  );
};

/** How many fields in one group carry an override, for the group's badge. */
export const overriddenCount = (fields, values) =>
  fields.filter((field) => isFieldOverridden(field, values?.[field.path]))
    .length;

/** The group badge text, or null when there is nothing to badge. */
export const overriddenLabel = (count) => {
  if (!count) return null;
  return count === 1 ? "1 променено" : `${count} променени`;
};

/** One-line preview of the committed content, used as the input placeholder. */
export const fallbackPreview = (kind, fallback) => {
  if (fallback === null || fallback === undefined) return "";
  if (typeof fallback === "string") return fallback;
  switch (kind) {
    case "list":
      return isListValue(fallback) ? `${fallback.items.length} елемента` : "";
    case "link":
      return [fallback.label, fallback.href].filter(Boolean).join(" — ");
    case "image":
      return fallback.alt || fallback.url || "";
    case "seo":
      return fallback.title || fallback.description || "";
    default:
      return "";
  }
};

export const ORIGINAL_PREVIEW_LIMIT = 100;

/**
 * The committed text as a single short line, shown under the input while
 * editing so the original stays readable next to the edit. Rich text is
 * flattened to its words — the reference is for comparison, not for markup.
 */
export const originalPreview = (
  kind,
  fallback,
  limit = ORIGINAL_PREVIEW_LIMIT,
) => {
  const text = fallbackPreview(kind, fallback)
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  if (!text) return "";
  return text.length > limit ? `${text.slice(0, limit).trimEnd()}…` : text;
};

/**
 * Everything about one field a search should be able to hit: its label, the
 * text it currently holds, and the committed text it falls back to. Composites
 * contribute every part, so searching for an href finds the link field.
 */
export const fieldSearchText = (field, value) => {
  if (field.kind === "list") {
    return [field.label, listSearchText(value), listSearchText(field.fallback)]
      .join(" ")
      .toLowerCase();
  }
  const parts = [field.label];
  const current = normaliseValue(field.kind, value);
  parts.push(
    typeof current === "string" ? current : Object.values(current).join(" "),
  );
  parts.push(fallbackPreview(field.kind, field.fallback));
  return parts
    .join(" ")
    .replace(/<[^>]*>/g, " ")
    .toLowerCase();
};

/**
 * The groups a search query leaves standing, each carrying only its matching
 * fields. An empty query returns the groups untouched — the caller renders the
 * same array either way, so there is one rendering path rather than two.
 */
export const filterGroups = (groups, values, query) => {
  const needle = String(query || "")
    .trim()
    .toLowerCase();
  if (!needle) return groups;

  return groups
    .map((group) => ({
      ...group,
      fields: group.fields.filter((field) =>
        fieldSearchText(field, values?.[field.path]).includes(needle),
      ),
    }))
    .filter((group) => group.fields.length > 0);
};

/**
 * What publishing would actually change, grouped by section.
 *
 * A true delta against the served snapshot, not a list of overrides: the editor
 * fetch already returns `publishedFields` alongside the draft, so comparing
 * against it costs nothing and answers the question Gloria is really asking —
 * "what will be different on the site?". A field that is overridden but already
 * published is not a change; a field that was published and has now been handed
 * back to the site is.
 */
export const publishChangeGroups = (manifest, values, publishedFields) => {
  const payload = payloadFromValues(manifest, values);
  const published = publishedFields || {};

  return groupFields(manifest)
    .map((group) => ({
      name: group.name,
      labels: group.fields
        .filter(
          (field) =>
            !field.locked &&
            !(field.kind === "list"
              ? sameListValue(payload[field.path], published[field.path])
              : valueEqualsFallback(
                  field.kind,
                  payload[field.path],
                  published[field.path],
                )),
        )
        .map((field) => field.label),
    }))
    .filter((group) => group.labels.length > 0);
};

/** Total fields across a `publishChangeGroups` result. */
export const publishChangeCount = (changeGroups) =>
  changeGroups.reduce((total, group) => total + group.labels.length, 0);

/** Manifest fields as `[{ group, fields[] }]`, in the order the page renders them. */
export const groupFields = (manifest) => {
  const groups = [];
  const byName = new Map();

  [...manifest.fields]
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    .forEach((field) => {
      const name = field.group || "Other";
      if (!byName.has(name)) {
        const group = { name, fields: [] };
        byName.set(name, group);
        groups.push(group);
      }
      byName.get(name).fields.push(field);
    });

  return groups;
};
